import React, { ComponentProps, useMemo } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { VatTransaction } from '../../graphql/entities';
import getDocumentModalProps from '../../utils/getDocumentModalProps';
import Formatters from '../../components/grid-addons/formatters';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import { ALLOW_VIEW } from '../../utils/OperationPermissions';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { Direction, IVatTransaction } from '../../graphql/schema/p4types';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { useVatTransactionActions } from '../../actions/vat/VatTransactionWorkspaceAction';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import { Paths } from '../../@types/keyOfNested';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';

const LazyFinancialTransactionModal = React.lazy(() => import('../../components/modals/FinancialTransactionModal'));

const VAT_TRANSACTION_COLUMN_OVERRIDES: IWorkspaceColumnOverride = {
  vatType: {
    name: 'VAT Type',
    key: 'vatType',
    formatter: Formatters.EnumFormatter,
    options: fieldMetaData.VatType,
    width: 80,
  },
  documentDate: {
    name: 'Date',
    key: 'documentDate',
    formatter: Formatters.DateFormatter,
    width: 130,
  },
  baseGoods: {
    name: 'Goods',
    formatter: Formatters.NumericFormatter,
    width: 120,
    options: {
      format: { decimals: 2 },
    },
  },
  rate: {
    name: 'VAT Rate',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2, suffix: '%' },
    },
    width: 120,
  },
  baseVat: {
    name: 'VAT Amount',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
    width: 120,
  },
  documentType: {
    name: 'Source Type',
    key: 'documentType',
    options: fieldMetaData.DocumentType,
    formatter: Formatters.EnumFormatter,
  },
  paymentType: {
    name: 'Payment Type',
    key: 'paymentType',
  },
  documentNumber: {
    name: 'Source No',
    formatter: ModalFormatter,
    width: 125,
    options: {
      modalContent: LazyFinancialTransactionModal,
      entityId: 'sourceId',
      modalProps: getDocumentModalProps,
    },
  },
  includedInVatReturn: {
    name: 'Inc.',
    width: 45,
  },
  vatReturnId: {
    name: 'VAT Return No.',
    width: 100,
  },
  'vatBusinessCategory.description': {
    name: 'Business VAT Status',
  },
  'vatProductCategory.description': {
    name: 'Product VAT Category',
  },
  ec: {
    name: 'EC',
    width: 45,
  },
  vat: {
    name: 'VAT',
  },
  reverseChargeVat: {
    name: 'Reverse Charge VAT',
  },
  pvaVat: {
    name: 'PVA VAT',
  },
  'vatAuthority.description': {
    name: 'VAT Authority',
  },
  pva: {
    name: 'PVA',
  },
  pvaRate: {
    name: 'PVA Rate',
  },
};

const VAT_TRANSACTION_FIELD_NAMES: Array<Paths<IVatTransaction>> = [
  'documentDate',
  'documentType',
  'documentNumber',
  'vatBusinessCategory.description',
  'vatProductCategory.description',
  'vatType',
  'ec',
  'includedInVatReturn',
  'vatReturnId',
  'baseGoods',
  'rate',
  'baseVat',
];

const { workspace, type, title } = VatTransaction;

const VatTransactionWorkspaceActions = (
  props: Omit<ComponentProps<typeof WorkspaceActionButtons>, 'actionButtons'>
) => {
  const [actions, modals] = useVatTransactionActions();

  return (
    <>
      {modals}
      <WorkspaceActionButtons {...props} actionButtons={actions} />
    </>
  );
};

const VatTransactionWorkspace = (props) => {
  const fieldNames: Array<Paths<IVatTransaction>> = useMemo(() => {
    if (props.fieldNameOverrides) {
      return props.fieldNameOverrides;
    }
    return VAT_TRANSACTION_FIELD_NAMES;
  }, [props.fieldNameOverrides]);

  const workspaceProps = useWorkspace<IVatTransaction>(props, VatTransaction, {
    columns: fieldNames,
    columnOverrides: VAT_TRANSACTION_COLUMN_OVERRIDES,
    initialSortColumn: 'documentDate',
    initialSortDirection: Direction.Desc,
  });

  return (
    <Workspace
      entityType={type}
      title={title}
      defaultQuery={workspace}
      permittedOperations={ALLOW_VIEW}
      {...workspaceProps}
      {...props}
      entity={VatTransaction}
      customBreadcrumb={[{ path: '/menu/Vat', title: 'VAT' }]}
      workspaceActionButtonsHOC={VatTransactionWorkspaceActions}
    />
  );
};

export default VatTransactionWorkspace;
